import React from "react";
import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { Modal as UIModal } from "src/components/ui";
import { useClient } from "src/store/hooks";
import LeanDeposit from "src/pages/LeanDeposit";
import Withdraw from "src/pages/Withdraw";
import { DEPOSIT_CASH_MODAL, WITHDRAW_CASH_MODAL } from "./ids";

/**
 * Depositing and withdrawing, as dialogs over wherever you are.
 *
 * Both were full pages reached from the sidebar, which meant leaving the
 * screen you were on to move money and then finding your way back to it. They
 * are short, single-purpose forms — an amount, an account, a confirmation —
 * which is what a dialog is for, and the product now has one dialog shell
 * with a titled head, a scrim and a footer band for exactly this.
 *
 * The flows themselves are untouched: the same steps, the same validation,
 * the same one-time-code step on a withdrawal. The pages take an `asDialog`
 * prop and drop only their own chrome — a page header and a page-width card,
 * neither of which a dialog needs — so there is one implementation of each
 * flow rather than two.
 *
 * The routes stay reachable. `/deposit` and `/withdraw` are linked from
 * elsewhere in the product and may be bookmarked, so they still work: they
 * land on the dashboard and open the same dialog over it.
 */

/** Wide enough for a select and an amount side by side, and no wider. */
const SIZE = "lg" as const;

export const DepositCashModal = NiceModal.create(() => {
  const modal = useModal();
  /* The same client the pages read when they are routes. */
  const user = useClient();

  return (
    <UIModal
      title="Deposit cash"
      description="Move money from your bank into your FinaMaze balance."
      size={SIZE}
      visible={modal.visible}
      onCancel={() => modal.hide()}
      afterClose={() => modal.remove()}
      footer={null}
    >
      <LeanDeposit user={user as any} asDialog onClose={() => modal.hide()} />
    </UIModal>
  );
});

export const WithdrawCashModal = NiceModal.create(() => {
  const modal = useModal();

  return (
    <UIModal
      title="Withdraw cash"
      description="Send money from your FinaMaze balance back to your own bank."
      size={SIZE}
      visible={modal.visible}
      onCancel={() => modal.hide()}
      afterClose={() => modal.remove()}
      footer={null}
    >
      <Withdraw asDialog onClose={() => modal.hide()} />
    </UIModal>
  );
});

/* Registered by id, so the sidebar can open either one without importing the
   flow it opens — these are the two heaviest pages in the app and the rail
   must not pull them in on first paint. The ids themselves live in ./ids, so
   the flows inside these dialogs can name them without importing this. */
export { DEPOSIT_CASH_MODAL, WITHDRAW_CASH_MODAL } from "./ids";

NiceModal.register(DEPOSIT_CASH_MODAL, DepositCashModal);
NiceModal.register(WITHDRAW_CASH_MODAL, WithdrawCashModal);
